import { forwardRef, useEffect, useImperativeHandle, useRef, useState, MouseEvent, useCallback } from 'react';
import { getStore, track } from './smi-bridge';
import { Provider } from 'react-redux';
import { CancelFlowController, CancelFlowControllerHandle } from './CancelFlowController';
import { CancelFlowConfig, CancelFlowNodeType } from './types';
import { ConfigContext } from './contexts/configContext';
import { CancelFlowIdContext } from './contexts/cancelFlowIdContext';
import { PreviewErrorBoundary } from './components/PreviewErrorBoundary';

/**
 * Render this component once and drive it imperatively via a ref.
 * Call open() and close() to open and close the dialog.
 */
export interface CancelFlowHandle {
  open: (subscriptionId: string) => void;
  close: () => void;
  navigateToStep: (nodeName: string) => void;
}

export interface CancelFlowProps {
  /** Called when the dialog is closed */
  onClose: () => void;
  config: CancelFlowConfig;
  cancelFlowId?: string;
  mode?: 'preview';
  /** Called whenever the current step changes. Receives null when the flow ends/resets. */
  onStepChange?: (step: { type: CancelFlowNodeType; name: string } | null) => void;
  onError?: (error: Error) => void;
}

const CancelFlow = forwardRef<CancelFlowHandle, CancelFlowProps>(
  ({ onClose: _onClose, config, cancelFlowId, mode, onStepChange, onError }, ref) => {
    const isPreviewMode = mode === 'preview';
    const store = getStore();
    const dialogRef = useRef<HTMLDialogElement>(null);
    const controllerRef = useRef<CancelFlowControllerHandle>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [subscriptionId, setSubscriptionId] = useState('');
    const [cancelReason, setCancelReason] = useState<string | undefined>(undefined);
    const [currentStep, setCurrentStep] = useState<CancelFlowNodeType | null>(null);

    const handleStepChange = useCallback(
      (step: { type: CancelFlowNodeType; name: string } | null) => {
        setCurrentStep(step?.type ?? null);
        onStepChange?.(step);
      },
      [onStepChange]
    );

    // combines the consumer's onClose callback with our internal state management
    const onClose = useCallback(() => {
      _onClose();
      setIsOpen(false);
      setCancelReason(undefined);
      setCurrentStep(null);
    }, [_onClose]);

    const closeDialog = useCallback(() => {
      if (isPreviewMode) return; // prevent closing dialog in preview mode
      // this should also trigger our close handler on the dialog and call `onClose`
      dialogRef.current?.close();
    }, [isPreviewMode]);

    // expose a ref so the consumer can imperatively open & close the dialog
    // this ensures that showModal executes before our internal state updates, to avoid effect timing issues
    useImperativeHandle(
      ref,
      () => ({
        open(subscriptionId: string) {
          dialogRef.current?.showModal();
          if (isPreviewMode) {
            // blur the default dialog focus in preview mode to avoid a focus outline on render
            dialogRef.current?.blur();
          }
          setSubscriptionId(subscriptionId);
          setIsOpen(true);
        },
        close() {
          closeDialog();
        },
        navigateToStep(nodeName: string) {
          controllerRef.current?.navigateToStep(nodeName);
        }
      }),
      [closeDialog, isPreviewMode]
    );

    // Handle body scroll prevention when modal is open
    useEffect(() => {
      if (isOpen && !isPreviewMode) {
        const originalStyle = document.documentElement.style.overflow;
        const originalScrollbarGutter = document.documentElement.style.scrollbarGutter;

        document.documentElement.style.scrollbarGutter = 'stable';
        document.documentElement.style.overflow = 'hidden';

        return () => {
          document.documentElement.style.overflow = originalStyle;
          document.documentElement.style.scrollbarGutter = originalScrollbarGutter;
        };
      }
    }, [isOpen, isPreviewMode]);

    // handle clicks on the backdrop (outside the dialog content)
    const handleBackdropClick = (event: MouseEvent<HTMLDialogElement>) => {
      const dialog = dialogRef.current;
      if (dialog && event.target === dialog) {
        // since we are outside the flow context, we can't use the useTrack hook and have to pass the attributes manually
        track('subscriptioncancel_exited', {
          subscriptionId,
          ...(cancelFlowId && { cancelFlowId }),
          ...(cancelReason && { cancelReason })
        });
        closeDialog();
      }
    };

    const content = (
      <Provider store={store}>
        <CancelFlowIdContext.Provider value={cancelFlowId}>
          <ConfigContext.Provider value={config}>
            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events */}
            <dialog
              ref={dialogRef}
              id="cancel-flow-dialog"
              className={currentStep === 'cancel-flow:sku-swap' ? 'og-dialog-wide' : ''}
              aria-labelledby="og-dialog-title"
              // if this fires, the dialog is already closing, so we don't need to call .close() on the dialog ourselves
              onClose={onClose}
              onClick={handleBackdropClick}
              // prevent closing the dialog with Esc in preview mode
              onCancel={isPreviewMode ? e => e.preventDefault() : undefined}
            >
              {isOpen && (
                <CancelFlowController
                  ref={controllerRef}
                  subscriptionId={subscriptionId}
                  isPreview={isPreviewMode}
                  onReset={closeDialog}
                  onCancelReasonChange={setCancelReason}
                  onStepChange={handleStepChange}
                />
              )}
            </dialog>
          </ConfigContext.Provider>
        </CancelFlowIdContext.Provider>
      </Provider>
    );

    // in preview mode, wrap in an error boundary so the editor can show error UI if something goes wrong
    return isPreviewMode ? <PreviewErrorBoundary onError={onError}>{content}</PreviewErrorBoundary> : content;
  }
);

CancelFlow.displayName = 'CancelFlow';

export default CancelFlow;
